import type { BimChapterEnum, FileInput } from 'gql/graphql';
import type { IListItem } from 'types/Contract';

import React, { useCallback, useMemo } from 'react';

import { BimUploadPriceFilesModal } from '../../BimCommonComponents/BimUploadPriceFilesModal';
import { useGetWorkOrderChangeObjects } from '../BimWorkOrderChange/hooks';

import { useUploadBimWorkOrderChangeFiles } from './hooks';

interface IBimWorkOrderChangeUploadPrices {
    onClose: () => void;
    refreshTable: () => void;
    workOrderChangeId: number;
    selectedObject?: IListItem<number>;
    bimChapter?: BimChapterEnum;
}

export const BimWorkOrderChangeUploadPrices = (props: IBimWorkOrderChangeUploadPrices) => {
    const { onClose, selectedObject, bimChapter, workOrderChangeId, refreshTable } = props;

    const { workOrderChangeObjectsData } = useGetWorkOrderChangeObjects({
        workOrderChangeId,
        resetAfterUnmount: false,
        withEffect: false,
    });

    const objectsListItems = useMemo(
        () =>
            workOrderChangeObjectsData?.map((object) => ({
                id: object.id,
                label: object.name,
                parentId: object.parentId,
                workOrderObjectId: object.workOrderObjectId,
                workOrderChangeObjectId: object.workOrderChangeObjectId,
            })),
        [workOrderChangeObjectsData],
    );

    const {
        bimUploadPriceFilesData,
        bimUploadPriceFilesStatus,
        bimUploadPriceFilesError,
        bimUploadPriceFilesMakeRequest,
        bimUploadPriceFilesReset,
        bimUploadPriceFilesAbort,
    } = useUploadBimWorkOrderChangeFiles();

    const makeUploadRequestHandler = useCallback(
        (vars: {
            chapter: BimChapterEnum | undefined;
            filesContent: FileInput[];
            workOrderObjectId: number | undefined;
            rationale?: string;
            currentSelectedObject?: IListItem<number>;
        }) => {
            const { chapter, filesContent, currentSelectedObject, rationale } = vars;
            const { workOrderObjectId, workOrderChangeObjectId } = currentSelectedObject ?? {};

            if (chapter && rationale) {
                bimUploadPriceFilesMakeRequest({
                    chapter,
                    filesContent,
                    workOrderChangeId,
                    workOrderChangeObjectId,
                    workOrderObjectId,
                    rationale,
                });
            }
        },
        [bimUploadPriceFilesMakeRequest, workOrderChangeId],
    );

    return (
        <BimUploadPriceFilesModal
            selectedObject={selectedObject}
            chapter={bimChapter}
            onClose={onClose}
            titleModal="Загрузка сметы"
            textInformer="Загружаемый файл сметы в формате xml будет относиться к выбранному объекту или подобъекту"
            uploadData={bimUploadPriceFilesData}
            uploadStatus={bimUploadPriceFilesStatus}
            uploadError={bimUploadPriceFilesError}
            uploadMakeRequest={makeUploadRequestHandler}
            uploadReset={bimUploadPriceFilesReset}
            uploadAbort={bimUploadPriceFilesAbort}
            objectsListItems={objectsListItems}
            successButtonTitle="Перейти в Изм. к НЗ"
            isConfirmDisabled={!workOrderChangeId}
            refreshProject={refreshTable}
            withRationale
        />
    );
};

BimWorkOrderChangeUploadPrices.displayName = 'BimWorkOrderChangeUploadPrices';
